import React, { useState, useMemo } from 'react';
import {
  Card,
  ScorePill,
  Delta,
  TH,
  TD,
  TD_BASE,
  TD_WRAP,
  Empty,
  HowCalculated,
  DefinitionTable,
  ago,
} from '../MetricsUI';
import { CountTile } from '../cohort/charts';
import Section from '../Section';
import { OrgLink, HealthPill, HealthHelp, ageLabel, fmtDay, RegionChips } from './adminShared';
import { SHOW_LEADERBOARDS } from '../metricsFlags';

/*
 * Section 1 — the book of business.
 *
 * Deliberately carries nothing commercial: no revenue, no plan tier, no renewal
 * date. "Account created" and its age replace "customer since / renews in",
 * because the age of the relationship is the part that helps read the numbers
 * beside it — a three-month-old account with 40% adoption is a different story
 * from a three-year-old one.
 */

export default function Portfolio({ data, onOpenOrg }) {
  const { organizations: orgs, portfolio: p, rankings, healthWeights, dataGaps } = data;
  const [sort, setSort] = useState({ key: 'health', dir: 'asc' });

  const rows = useMemo(() => {
    const val = (o) => {
      switch (sort.key) {
        case 'name':
          return o.name || '';
        case 'created':
          return o.account.createdAt;
        case 'members':
          return o.members;
        case 'seats':
          return o.seats;
        case 'adoption':
          return o.adoptionPct;
        case 'chapters':
          return o.avgChapterPct;
        case 'score':
          return o.medianScore;
        case 'improvement':
          return o.avgImprovement;
        case 'recency':
          // Sorted on the timestamp, so "quietest account" is one click.
          return o.lastActivityAt;
        case 'health':
          return o.health.score;
        default:
          return 0;
      }
    };
    return [...orgs].sort((a, b) => {
      const x = val(a);
      const y = val(b);
      if (x == null && y == null) return 0;
      if (x == null) return 1;
      if (y == null) return -1;
      const cmp = typeof x === 'string' ? x.localeCompare(y) : x - y;
      return sort.dir === 'asc' ? cmp : -cmp;
    });
  }, [orgs, sort]);

  const th = (key, label, extra = '') => (
    <th
      onClick={() => setSort((s) => ({ key, dir: s.key === key && s.dir === 'asc' ? 'desc' : 'asc' }))}
      className={`cursor-pointer select-none hover:text-navy ${TH} ${extra}`}
      title="Click to sort; click again to reverse"
    >
      {label}
      {sort.key === key && <span className="ml-1 text-lavender">{sort.dir === 'asc' ? '▲' : '▼'}</span>}
    </th>
  );

  const onTargetAdoption = orgs.filter(
    (o) => o.adoptionPct != null && o.adoptionPct >= p.targets.adoptionPct
  );
  const onTargetImprovement = orgs.filter(
    (o) => o.avgImprovement != null && o.avgImprovement >= p.targets.improvementPts
  );
  const neverStartedOrgs = orgs.filter((o) => o.everStarted === 0);

  // Sorted, so hovering a median shows the distribution it came from rather than
  // an arbitrary order.
  const adoptionItems = orgs
    .filter((o) => o.adoptionPct != null)
    .map((o) => ({ ...o, value: o.adoptionPct }))
    .sort((a, b) => b.value - a.value);
  const improvementItems = orgs
    .filter((o) => o.avgImprovement != null)
    .map((o) => ({ ...o, value: o.avgImprovement }))
    .sort((a, b) => b.value - a.value);

  return (
    <Section
      title="Portfolio"
      subtitle="Every customer organisation — click any name to drop into its manager view"
      summary={
        <div className="flex flex-wrap gap-4">
          <CountTile
            label="Organisations"
            value={p.orgs}
            hint={`${p.members} people`}
            items={orgs}
            onOpen={(id) => onOpenOrg(orgs.find((o) => o.organizationId === id))}
          />
          <CountTile
            label="Nobody has started"
            value={neverStartedOrgs.length}
            tone={neverStartedOrgs.length ? 'warn' : 'good'}
            hint={`of ${p.orgs} organisations`}
            items={neverStartedOrgs}
            onOpen={(id) => onOpenOrg(orgs.find((o) => o.organizationId === id))}
          />
          <CountTile
            label="On the adoption target"
            value={onTargetAdoption.length}
            unit={`/ ${p.onTargetAdoptionOf}`}
            tone={onTargetAdoption.length ? 'good' : 'default'}
            hint={`${p.targets.adoptionPct}% active in 30 days`}
            items={onTargetAdoption}
            onOpen={(id) => onOpenOrg(orgs.find((o) => o.organizationId === id))}
          />
          <CountTile
            label="On the improvement target"
            value={onTargetImprovement.length}
            unit={`/ ${p.onTargetImprovementOf}`}
            tone={onTargetImprovement.length ? 'good' : 'default'}
            hint={`+${p.targets.improvementPts} pts or better`}
            items={onTargetImprovement}
            onOpen={(id) => onOpenOrg(orgs.find((o) => o.organizationId === id))}
          />
          {/* Both medians hover to the organisations behind them, each carrying its
              own value — a median is only actionable if you can see the spread it
              came from. */}
          <CountTile
            label="Median org adoption"
            value={p.adoption.median == null ? '—' : `${Math.round(p.adoption.median)}%`}
            tone="accent"
            hint={`n=${p.adoption.n}`}
            unit="%"
            items={adoptionItems}
            onOpen={(id) => onOpenOrg(orgs.find((o) => o.organizationId === id))}
            help={
              <ul className="ml-4 list-disc space-y-1">
                <li>One number per organisation: its share of members active in 30 days.</li>
                <li>The median of those, so one large customer does not set the tone.</li>
                <li>Organisations with no members at all are not counted.</li>
              </ul>
            }
          />
          <CountTile
            label="Median org improvement"
            value={
              p.improvement.median == null
                ? '—'
                : `${p.improvement.median > 0 ? '+' : ''}${Math.round(p.improvement.median)} pts`
            }
            hint={`n=${p.improvement.n}`}
            unit=" pts"
            items={improvementItems}
            onOpen={(id) => onOpenOrg(orgs.find((o) => o.organizationId === id))}
            help={
              <ul className="ml-4 list-disc space-y-1">
                <li>One number per organisation: its average per-learner improvement, in points.</li>
                <li>Only organisations where somebody retried a case study can be measured.</li>
                <li>The rest are excluded rather than counted as zero.</li>
              </ul>
            }
          />
        </div>
      }
    >
      <Card className="overflow-hidden p-0">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-4 py-3">
          <p className="text-sm font-semibold text-navy">
            Book of business
            <HowCalculated wide label="Reading this table">
              <DefinitionTable
                rows={[
                  ['Account created', 'When the organisation record was created, and how long ago'],
                  ['Members', 'People with an account, deduplicated by email'],
                  ['Seats', 'Paid seats from the subscription on record (features.maxUsers); — when the registry holds no subscription'],
                  ['Adoption', 'Share of members active in the last 30 days'],
                  ['Last activity', 'When anyone in the organisation last used the app — the most recent, not the typical'],
                  ['Health', 'Composite, renormalised over the components that have data'],
                ]}
              />
            </HowCalculated>
          </p>
          {dataGaps.seats.missing > 0 && (
            <span className="text-xs text-gray-400">
              {dataGaps.seats.missing} of {dataGaps.seats.of} organisations have no subscription on record
            </span>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[1180px]">
            <thead className="bg-gray-50">
              <tr>
                {th('name', 'Organisation')}
                <th className={`w-32 ${TH}`}>Region</th>
                {th('created', 'Account created', 'w-40')}
                {th('members', 'Members', 'w-24')}
                {th('seats', 'Seats', 'w-20')}
                {th('adoption', 'Adoption', 'w-28')}
                {th('chapters', 'Chapters', 'w-24')}
                {th('score', 'Median score', 'w-28')}
                {th('improvement', 'Improvement', 'w-32')}
                {th('recency', 'Last activity', 'w-32')}
                {th('health', 'Health', 'w-24')}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((o) => (
                <tr key={o.organizationId} className="hover:bg-indigo-50/50">
                  <td className={TD_WRAP}>
                    <OrgLink org={o} onOpenOrg={onOpenOrg} />
                    <p className="text-xs text-gray-400">
                      {o.chapterGroup} content
                      {o.learningPathEnabled ? ' · levels on' : ''}
                    </p>
                  </td>
                  <td className={`${TD_BASE} text-gray-500`}>
                    <RegionChips regions={o.regions} />
                  </td>
                  <td className={`${TD_BASE} text-gray-600`}>
                    {fmtDay(o.account.createdAt)}
                    <span className="ml-1 text-xs text-gray-400">
                      {o.account.months != null && `· ${ageLabel(o.account.months)}`}
                    </span>
                    {o.account.source === 'firstMember' && (
                      <span
                        className="ml-1 text-[10px] text-amber-600"
                        title="No timestamp on the organisation record; this is the oldest member account"
                      >
                        approx
                      </span>
                    )}
                  </td>
                  <td className={`${TD_BASE} tabular-nums text-gray-700`}>{o.members}</td>
                  <td className={`${TD_BASE} tabular-nums text-gray-500`}>{o.seats ?? '—'}</td>
                  <td className={TD}>
                    {o.adoptionPct == null ? (
                      '—'
                    ) : (
                      <span className="flex items-center gap-2">
                        <span className="h-1.5 w-14 overflow-hidden rounded-full bg-gray-100">
                          <span
                            className="block h-full rounded-full bg-lavender"
                            style={{ width: `${o.adoptionPct}%` }}
                          />
                        </span>
                        <span className="text-xs tabular-nums text-gray-600">{o.adoptionPct}%</span>
                      </span>
                    )}
                  </td>
                  <td className={`${TD_BASE} tabular-nums text-gray-600`}>
                    {o.avgChapterPct == null ? '—' : `${o.avgChapterPct}%`}
                  </td>
                  <td className={TD}>
                    <ScorePill pct={o.medianScore} />
                  </td>
                  <td className={TD}>
                    <Delta value={o.avgImprovement} />
                    {o.measurableN > 0 && (
                      <span className="ml-1 text-[10px] text-gray-400">n={o.measurableN}</span>
                    )}
                  </td>
                  <td className={`${TD_BASE} text-gray-600`}>
                    {o.lastActivityAt == null ? '—' : ago(o.lastActivityAt)}
                  </td>
                  <td className={TD}>
                    <HealthPill health={o.health} />
                    {!o.rankable && (
                      <span className="ml-1 text-[10px] text-gray-400" title="Too few active members to rank">
                        low n
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={11}>
                    <Empty>No organisations with members.</Empty>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="border-t border-gray-100 px-4 py-2 text-xs text-gray-400">
          Health <HealthHelp health={rows[0] && rows[0].health} weights={healthWeights} />
        </div>
      </Card>

      {/* Org-vs-org ranking, hidden for now — see metricsFlags.js */}
      {SHOW_LEADERBOARDS && <Rankings rankings={rankings} orgs={orgs} onOpenOrg={onOpenOrg} />}
    </Section>
  );
}

/**
 * Three org-level boards, each gated on n.
 *
 * The gate is the point: an organisation with two enthusiastic learners would
 * otherwise top every ranking, and "highest adoption: 100%" of two people is
 * not a fact anyone should act on.
 */
function Rankings({ rankings, orgs, onOpenOrg }) {
  const byId = new Map(orgs.map((o) => [o.organizationId, o]));
  const boards = [
    {
      title: 'Highest adoption',
      note: 'Share of members active in the last 30 days',
      rows: rankings.highestAdoption,
      render: (v) => <span className="text-sm font-semibold tabular-nums text-navy">{Math.round(v)}%</span>,
    },
    {
      title: 'Most improvement',
      note: 'The ranking that reflects whether the product is working',
      rows: rankings.mostImprovement,
      render: (v) => (
        <span className="text-sm font-semibold tabular-nums text-emerald-600">▲ {Math.round(v)} pts</span>
      ),
    },
    {
      title: 'Most intensive use',
      note: 'Minutes per active member, so a big org does not win by size',
      rows: rankings.mostIntensive,
      render: (v) => (
        <span className="text-sm font-semibold tabular-nums text-navy">{Math.round(v / 60)}h</span>
      ),
    },
  ];

  return (
    <div>
      <p className="mb-3 text-xs text-gray-500">
        Ranked over the {rankings.pool} organisations with at least {rankings.minN} active members
        {rankings.excluded > 0 && ` · ${rankings.excluded} excluded for low n`}
      </p>
      <div className="grid gap-6 lg:grid-cols-3">
        {boards.map((b) => (
          <Card key={b.title}>
            <h4 className="text-lg font-semibold text-navy">{b.title}</h4>
            <p className="mb-3 text-xs text-gray-500">{b.note}</p>
            {b.rows.length === 0 ? (
              <Empty>Not enough data yet.</Empty>
            ) : (
              <ol className="space-y-2.5">
                {b.rows.map((r, i) => (
                  <li key={r.organizationId} className="flex items-center gap-3 text-sm">
                    <span className="w-4 text-xs tabular-nums text-gray-400">{i + 1}</span>
                    <span className="flex-1 truncate">
                      <OrgLink org={byId.get(r.organizationId) || r} onOpenOrg={onOpenOrg} />
                    </span>
                    <span className="text-xs text-gray-400">n={r.basis.activeLast30}</span>
                    {b.render(r.value)}
                  </li>
                ))}
              </ol>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}
